import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { formatAIChatTime } from '../../components/Succession/Messaging/shared/dateUtils';
import Button from '../../../../ui/canon/Button.jsx';
import Input from '../../../../ui/canon/Input.jsx';
import classNames from 'classnames';
import { SparklesIcon } from '@heroicons/react/24/solid';
import GeorgeIcon from '../../components/icons/GeorgeIcon.jsx';
import { Loader2 as Loader2Icon, Send as SendIcon, BookOpen as BookOpenIcon } from 'lucide-react';
import MarkdownMessage from './MarkdownMessage.jsx';
import CollapsibleSources from './CollapsibleSources.jsx';
import CopyMessageButton from '../../components/Succession/AgentSystem/CopyMessageButton';
import { georgeTopics } from '../data/assistantData';

// Wrapper component for assistant message content that provides a ref for copying
const AssistantMessageContent = React.forwardRef(({ message }, ref) => {
  const intl = useIntl();
  return (
    <div ref={ref}>
      <div
        className="chat_result text-succession-text-primary"
        dangerouslySetInnerHTML={{ __html: message.content }}
      />
      {/* Render widgets if present */}
      {message.widgets && message.widgets.length > 0 && (
        <div className="mt-3 space-y-2">
          {message.widgets.map((widget, idx) => (
            <div key={idx} className="bg-succession-bg-secondary border border-succession-border rounded-md p-3">
              <div className="text-xs font-semibold text-succession-text-primary mb-2">{widget.data.title || widget.type}</div>
              {widget.type === 'valuation_summary' && (
                <div className="space-y-2 text-xs">
                  {/* New format from MultiLineValuationChatAgent */}
                  {widget.data.total && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'assistant.widgets.total_practice_value', defaultMessage: 'Total Practice Value:' })}</span>
                        <span className="font-medium text-succession-text-primary text-lg">{widget.data.total}</span>
                      </div>
                      {widget.data.range && (
                        <div className="flex justify-between text-succession-text-secondary">
                          <span>{intl.formatMessage({ id: 'assistant.widgets.range', defaultMessage: 'Range:' })}</span>
                          <span>{widget.data.range.low} - {widget.data.range.high}</span>
                        </div>
                      )}
                      {widget.data.lines && widget.data.lines.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-succession-border">
                          <div className="text-xs font-medium mb-1">{intl.formatMessage({ id: 'assistant.widgets.by_business_line', defaultMessage: 'By Business Line:' })}</div>
                          {widget.data.lines.map((line, lineIdx) => (
                            <div key={lineIdx} className="flex justify-between">
                              <span className="text-succession-text-secondary">{line.name}:</span>
                              <span className="font-medium">{line.value} ({line.percentage})</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {/* Legacy format */}
                  {widget.data.data && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'assistant.widgets.practice_value', defaultMessage: 'Practice Value:' })}</span>
                        <span className="font-medium text-succession-text-primary">{widget.data.data.value}</span>
                      </div>
                      {widget.data.data.ebit && (
                        <div className="flex justify-between">
                          <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'assistant.widgets.ebit', defaultMessage: 'EBIT:' })}</span>
                          <span className="font-medium text-succession-text-primary">{widget.data.data.ebit}</span>
                        </div>
                      )}
                      {widget.data.data.revenue && (
                        <div className="flex justify-between">
                          <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'assistant.widgets.revenue', defaultMessage: 'Revenue:' })}</span>
                          <span className="font-medium text-succession-text-primary">{widget.data.data.revenue}</span>
                        </div>
                      )}
                    </>
                  )}
                  <a
                    href="/succession/valuation/multi"
                    className="inline-block mt-2 text-xs text-succession-secondary hover:text-succession-secondary-dark underline"
                  >
                    {intl.formatMessage({ id: 'assistant.widgets.view_full_valuation', defaultMessage: 'View Full Valuation →' })}
                  </a>
                </div>
              )}
              {widget.type === 'method_comparison' && widget.data.methods && (
                <div className="space-y-3 text-xs">
                  {widget.data.line && (
                    <div className="text-sm font-medium text-succession-text-primary">
                      {intl.formatMessage({ id: 'assistant.widgets.method_comparison_title', defaultMessage: '{line} Line - Method Comparison' }, { line: widget.data.line })}
                    </div>
                  )}
                  <div className="space-y-2">
                    {widget.data.methods.map((method, methodIdx) => (
                      <div key={methodIdx} className="flex items-center justify-between py-1 border-b border-succession-border last:border-0">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-succession-text-primary">{method.name}</span>
                            <span className="text-succession-text-secondary">({method.weight})</span>
                          </div>
                          {method.description && (
                            <div className="text-succession-text-secondary text-[10px] mt-0.5">{method.description}</div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-succession-text-primary">{method.value}</div>
                          {method.range && method.range !== '$0 - $0' && (
                            <div className="text-succession-text-secondary text-[10px]">{method.range}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {widget.data.blended_value && (
                    <div className="pt-2 border-t-2 border-succession-border flex justify-between items-center">
                      <span className="font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'assistant.widgets.blended_value', defaultMessage: 'Blended Value:' })}</span>
                      <span className="font-bold text-lg text-succession-text-primary">{widget.data.blended_value}</span>
                    </div>
                  )}
                  {widget.data.insight && (
                    <div className="mt-2 p-2 bg-succession-bg-tertiary rounded text-succession-text-secondary text-[11px]">
                      💡 {widget.data.insight}
                    </div>
                  )}
                  <a
                    href="/succession/valuation/multi"
                    className="inline-block mt-2 text-xs text-succession-secondary hover:text-succession-secondary-dark underline"
                  >
                    {intl.formatMessage({ id: 'assistant.widgets.view_full_valuation', defaultMessage: 'View Full Valuation →' })}
                  </a>
                </div>
              )}
              {widget.type === 'scenario_comparison' && (
                <div className="space-y-3 text-xs">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-succession-bg-tertiary p-2 rounded text-center">
                      <div className="text-succession-text-secondary text-[10px] uppercase">{intl.formatMessage({ id: 'assistant.widgets.before', defaultMessage: 'Before' })}</div>
                      <div className="text-base font-bold text-succession-text-primary">{widget.data.before}</div>
                    </div>
                    <div className="bg-succession-bg-tertiary p-2 rounded text-center">
                      <div className="text-succession-text-secondary text-[10px] uppercase">{intl.formatMessage({ id: 'assistant.widgets.after', defaultMessage: 'After' })}</div>
                      <div className="text-base font-bold text-succession-success">{widget.data.after}</div>
                    </div>
                    <div className="bg-succession-bg-tertiary p-2 rounded text-center">
                      <div className="text-succession-text-secondary text-[10px] uppercase">{intl.formatMessage({ id: 'assistant.widgets.impact_header', defaultMessage: 'Impact' })}</div>
                      <div className={`text-base font-bold ${widget.data.changeIsPositive ? 'text-succession-success' : 'text-succession-error'}`}>
                        {widget.data.change}
                      </div>
                    </div>
                  </div>
                  {widget.data.line_impact && (
                    <div className="text-succession-text-secondary border-t border-succession-border pt-2">
                      <span className="font-medium">{widget.data.line_impact.name}:</span> {widget.data.line_impact.before} → {widget.data.line_impact.after} ({widget.data.line_impact.change})
                    </div>
                  )}
                  {widget.data.changes_applied && widget.data.changes_applied.length > 0 && (
                    <div className="text-[10px] text-succession-text-secondary">
                      {intl.formatMessage({ id: 'assistant.widgets.changes', defaultMessage: 'Changes:' })} {widget.data.changes_applied.join(' • ')}
                    </div>
                  )}
                  {widget.data.note && (
                    <div className="text-[10px] text-succession-text-secondary italic">{widget.data.note}</div>
                  )}
                </div>
              )}
              {widget.type === 'recommendations' && (
                <div className="space-y-3 text-xs">
                  {widget.data.current_valuation && (
                    <div className="flex justify-between items-center pb-2 border-b border-succession-border">
                      <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'assistant.widgets.current_value', defaultMessage: 'Current Value:' })}</span>
                      <span className="font-bold text-succession-text-primary">{widget.data.current_valuation}</span>
                    </div>
                  )}
                  <div className="space-y-2">
                    {widget.data.items?.map((item, itemIdx) => (
                      <div key={itemIdx} className="flex gap-2 p-2 bg-succession-bg-tertiary rounded">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                          item.priority === 1 ? 'bg-succession-error/20 text-succession-error' :
                          item.priority === 2 ? 'bg-succession-warning/20 text-succession-warning' :
                          'bg-succession-success/20 text-succession-success'
                        }`}>
                          {item.priority}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-succession-text-primary">{item.title}</span>
                            {item.line && item.line !== 'General' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-succession-secondary/10 text-succession-secondary rounded-full">
                                {item.line}
                              </span>
                            )}
                          </div>
                          <div className="text-succession-text-secondary mt-0.5">{item.description}</div>
                          <div className="flex items-center gap-3 mt-1">
                            {item.impact && (
                              <span className={`text-[10px] font-medium ${
                                item.impact === 'High' ? 'text-succession-success' :
                                item.impact === 'Medium' ? 'text-succession-warning' :
                                'text-succession-text-secondary'
                              }`}>
                                {intl.formatMessage({ id: 'assistant.widgets.impact', defaultMessage: 'Impact:' })} {item.impact}
                              </span>
                            )}
                            {item.estimated_value_add && (
                              <span className="text-[10px] text-succession-success font-medium">
                                +{item.estimated_value_add}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {widget.data.total_potential && widget.data.total_potential !== '$0' && (
                    <div className="flex justify-between items-center pt-2 border-t border-succession-border">
                      <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'assistant.widgets.total_potential_value_add', defaultMessage: 'Total Potential Value Add:' })}</span>
                      <span className="font-bold text-succession-success">{widget.data.total_potential}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// Wrapper for assistant message bubble with copy functionality
const AssistantMessageBubble = ({ message, onSendMessage }) => {
  const contentRef = React.useRef(null);

  return (
    <div className="rounded-lg p-4 max-w-[80%] bg-succession-bg-primary border border-succession-border relative group">
      <AssistantMessageContent ref={contentRef} message={message} />

      {/* RAG Sources display - collapsible if too many */}
      <CollapsibleSources sources={message.ragSources} collapsedLimit={2} />

      {/* Suggested actions */}
      {message.suggestedActions && message.suggestedActions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {message.suggestedActions.map((action, idx) => (
            <button
              key={idx}
              onClick={() => onSendMessage && onSendMessage(action)}
              className="text-xs px-2 py-1 rounded bg-succession-secondary/10 text-succession-secondary hover:bg-succession-secondary/20 transition-colors"
            >
              {action}
            </button>
          ))}
        </div>
      )}

      {/* Timestamp */}
      <p className="text-xs mt-2 m-0 p-0 text-succession-text-secondary">
        {(() => {
          const formatted = formatAIChatTime(message.timestamp);
          return formatted === 'Invalid date' ? message.timestamp : formatted;
        })()}
      </p>

      {/* Copy button below timestamp */}
      <CopyMessageButton
        containerRef={contentRef}
        isHtml={true}
        className="mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    </div>
  );
};

export default function AssistantChat({ messages = [], onSendMessage, suggestedTopics = [], initialPrompt = '', isThinking = false, className, companyBrandedName, georgeCoachEnabled = false }) {
  const intl = useIntl();
  const [input, setInput] = useState(initialPrompt);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const forceScrollRef = useRef(false);
  const pinnedRef = useRef(true);

  const scrollToBottom = (smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  useEffect(() => {
    if (initialPrompt) setInput(initialPrompt);
  }, [initialPrompt]);

  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
      if (atBottom || forceScrollRef.current || pinnedRef.current) {
        scrollToBottom(!atBottom);
        forceScrollRef.current = false;
      }
    }
  }, [messages, isThinking]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
      setShowScrollToBottom(!atBottom);
      pinnedRef.current = atBottom;
    };
    el.addEventListener('scroll', onScroll);
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll when thinking begins so the user sees the placeholder
  useEffect(() => {
    if (isThinking) {
      pinnedRef.current = true;
      scrollToBottom(true);
    }
  }, [isThinking]);

  // If the messages container grows (e.g., assistant streams or expands), keep pinned to bottom
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (pinnedRef.current) {
        scrollToBottom(false);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isEmpty = messages.length === 0 && !isThinking;

  const handleSend = () => {
    const text = (input || '').trim();
    if (!text || !onSendMessage) return;
    onSendMessage(text);
    setInput('');
    // Force scroll to the latest message and upcoming thinking state
    forceScrollRef.current = true;
    pinnedRef.current = true;
    setTimeout(() => scrollToBottom(true), 0);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTopicClick = (title) => {
    if (onSendMessage) onSendMessage(title);
  };

  const brandName = companyBrandedName || (typeof document !== 'undefined' && document.store?.getState?.()?.company?.branded_name) || 'Continuum';
  const disclaimerText = intl.formatMessage({ id: 'assistant.chat.disclaimer' }, { brand: brandName });

  // Empty state - input at top center with suggestions below
  if (isEmpty) {
    return (
      <div ref={containerRef} className={classNames('flex flex-col h-full min-h-0 max-h-full overflow-hidden relative', className)}>
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-3xl mx-auto pt-8 md:pt-16">
            {/* Header */}
            <div className="text-center space-y-3 mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-succession-secondary/10">
                {georgeCoachEnabled
                  ? <GeorgeIcon className="h-8 w-8 text-succession-secondary" />
                  : <SparklesIcon className="h-8 w-8 text-succession-secondary" />}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-succession-text-primary m-0 p-0">
                {intl.formatMessage({ id: georgeCoachEnabled ? 'assistant.chat.george_empty_heading' : 'assistant.chat.empty_heading' })}
              </h2>
              <p className="text-succession-text-secondary text-sm md:text-base max-w-prose mx-auto">
                {intl.formatMessage({ id: georgeCoachEnabled ? 'assistant.chat.george_empty_subtitle' : 'assistant.chat.empty_subtitle' })}
              </p>
            </div>

            {/* Centered Input */}
            <div className="mb-10">
              <div className="w-full max-w-2xl mx-auto">
                <div className="relative">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={intl.formatMessage({ id: georgeCoachEnabled ? 'assistant.chat.george_input_placeholder' : 'assistant.chat.input_placeholder' })}
                    rows={1}
                    className="w-full min-h-[52px] max-h-[200px] pr-12 resize-none rounded-lg bg-white border-2 border-succession-secondary/30 text-succession-text-primary placeholder-succession-text-muted px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring shadow-md"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={!input.trim() || isThinking}
                    size="sm"
                    className="absolute right-2 h-8 w-8 p-0 rounded-full"
                    style={{ marginTop: 11 }}
                  >
                    <SendIcon className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-succession-text-secondary text-center mt-2">
                  {disclaimerText}
                </p>
              </div>
            </div>

            {/* Suggested Topics */}
            {((georgeCoachEnabled ? georgeTopics : suggestedTopics).length > 0) && (
              <div className="space-y-4">
                <p className="text-sm font-medium text-succession-text-secondary text-center">{intl.formatMessage({ id: 'assistant.chat.suggested_topics_heading' })}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                  {(georgeCoachEnabled ? georgeTopics : suggestedTopics).map((topic) => {
                    const translatedTitle = intl.formatMessage({ id: `assistant.topics.${topic.id}.title`, defaultMessage: topic.title });
                    return (
                    <button
                      key={topic.id}
                      onClick={() => handleTopicClick(translatedTitle)}
                      className={classNames(
                        'p-4 text-left rounded-lg border border-succession-border',
                        'bg-succession-bg-primary hover:bg-succession-bg-secondary',
                        'transition-all duration-300 hover:shadow-succession-lg',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring'
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-5 w-5 rounded bg-succession-secondary/10 flex items-center justify-center mt-0.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-succession-secondary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-succession-text-primary m-0 p-0">{intl.formatMessage({ id: `assistant.topics.${topic.id}.title`, defaultMessage: topic.title })}</p>
                          <p className="text-xs text-succession-text-secondary mt-1">{intl.formatMessage({ id: `assistant.topics.${topic.id}.description`, defaultMessage: topic.description })}</p>
                        </div>
                      </div>
                    </button>
                  );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Active conversation state - input at bottom
  return (
    <div ref={containerRef} className={classNames('flex flex-col h-full min-h-0 max-h-full overflow-hidden relative', className)}>
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0 pb-4 relative">
        <div className="max-w-3xl mx-auto space-y-6">
          {messages.map((m) => (
            <div key={m.id} className={classNames('flex gap-4', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                {/* Avatar for assistant */}
                {m.role === 'assistant' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-secondary/10 flex items-center justify-center">
                    {georgeCoachEnabled
                      ? <GeorgeIcon className="h-4 w-4 text-succession-secondary" />
                      : <SparklesIcon className="h-4 w-4 text-succession-secondary" />}
                  </div>
                )}

                {/* Message Content */}
                {m.role === 'user' ? (
                  <div className="rounded-lg p-4 max-w-[80%] bg-succession-secondary text-white">
                    <p className="text-sm whitespace-pre-wrap m-0 p-0 text-white">{m.content}</p>
                    {/* Timestamp inside bubble */}
                    <p className="text-xs mt-2 m-0 p-0 text-white/70">
                      {(() => {
                        const formatted = formatAIChatTime(m.timestamp);
                        return formatted === 'Invalid date' ? m.timestamp : formatted;
                      })()}
                    </p>
                  </div>
                ) : (
                  <AssistantMessageBubble message={m} onSendMessage={onSendMessage} />
                )}

                {/* Avatar for user */}
                {m.role === 'user' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-bg-secondary flex items-center justify-center">
                    <span className="text-xs font-medium text-succession-text-primary">{intl.formatMessage({ id: 'assistant.chat.you_label' })}</span>
                  </div>
                )}
              </div>
            ))}

            {isThinking && (
              <div className="flex gap-4 justify-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-secondary/10 flex items-center justify-center">
                  {georgeCoachEnabled
                    ? <GeorgeIcon className="h-4 w-4 text-succession-secondary" />
                    : <SparklesIcon className="h-4 w-4 text-succession-secondary" />}
                </div>
                <div className="rounded-lg p-4 bg-succession-bg-primary border border-succession-border">
                  <div className="flex items-center gap-2">
                    <Loader2Icon className="h-4 w-4 text-succession-secondary animate-spin" />
                    <p className="text-sm text-succession-text-secondary m-0 p-0">{intl.formatMessage({ id: 'assistant.chat.thinking' })}</p>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Scroll to bottom affordance */}
      {showScrollToBottom && (
        <button
          aria-label={intl.formatMessage({ id: 'assistant.chat.scroll_to_latest' })}
          onClick={() => {
            if (scrollRef.current) scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
          }}
          className="absolute right-6 bottom-24 z-10 rounded-full bg-succession-bg-primary border border-succession-border shadow-succession-md p-2 hover:bg-succession-bg-secondary"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-succession-text-primary">
            <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v12.19l3.47-3.47a.75.75 0 111.06 1.06l-4.75 4.75a.75.75 0 01-1.06 0l-4.75-4.75a.75.75 0 111.06-1.06l3.47 3.47V4.5A.75.75 0 0112 3.75z" clipRule="evenodd" />
          </svg>
        </button>
      )}

      {/* Input */}
      <div className="border-t-2 border-succession-border p-4 flex-shrink-0" style={{ backgroundColor: '#f1f5f9', boxShadow: '0 -8px 24px rgba(0,0,0,0.08)' }}>
        <div className="w-full max-w-3xl mx-auto">
          <div className="relative">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={intl.formatMessage({ id: 'assistant.chat.input_placeholder', defaultMessage: 'Ask the Assistant a question' })}
              rows={1}
              className="w-full min-h-[52px] max-h-[200px] pr-12 resize-none rounded-lg bg-white border-2 border-succession-secondary/30 text-succession-text-primary placeholder-succession-text-muted px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring shadow-md"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isThinking}
              size="sm"
              className="absolute right-2 h-8 w-8 p-0 rounded-full"
              style={{ marginTop: 11 }}
            >
              <SendIcon className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-succession-text-secondary text-center mt-2">
            {disclaimerText}
          </p>
        </div>
      </div>
    </div>
  );
}


